import { useState, useEffect, useRef } from 'react';
import PDFUpload from './components/PDFUpload';
import PDFPreview from './components/PDFPreview';
import TransformationIndicator from './components/TransformationIndicator';
import Toast, { ToastType } from './components/Toast';
import { uploadFullPDF, pollJobStatus, JobStatusResponse } from './utils/api';

interface ToastState {
  message: string;
  type: ToastType;
  isVisible: boolean;
}

function App() {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [toast, setToast] = useState<ToastState>({
    message: '',
    type: 'info',
    isVisible: false,
  });
  // Use refs to track cancellation and active processing
  const abortControllerRef = useRef<AbortController | null>(null);
  const isProcessingRef = useRef<boolean>(false);

  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const handleFileSelect = (file: File) => {
    setPdfFile(file);
    setExtractError(null);
    setJobStatus(null);
  };

  const handleUploadError = (message: string) => {
    showToast(message, 'error');
  };

  const handleExtract = async (selectedPages: number[]) => {
    if (!pdfFile || selectedPages.length === 0) return;

    // Cancel any existing polling
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this extraction
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    isProcessingRef.current = true;

    setIsExtracting(true);
    setExtractError(null);
    setJobStatus(null);

    try {
      // Always send full PDF to backend for LLMWhisperer processing
      const jobId = await uploadFullPDF(pdfFile, selectedPages);

      // Check if cancelled before starting to poll
      if (abortController.signal.aborted || !isProcessingRef.current) {
        // Clean up state if cancelled
        if (abortControllerRef.current === abortController) {
          setIsExtracting(false);
          isProcessingRef.current = false;
        }
        return;
      }

      // Poll for job status
      const finalStatus = await pollJobStatus(
        jobId,
        (status) => {
          // Only update status if still processing
          if (isProcessingRef.current && !abortController.signal.aborted) {
            setJobStatus(status);
          }
        },
        2000, // Poll every 2 seconds
        abortController.signal
      );

      // Only update state if still processing and not cancelled
      if (isProcessingRef.current && !abortController.signal.aborted) {
        setJobStatus(finalStatus);

        if (finalStatus.status === 'failed') {
          const errorMsg = finalStatus.error || 'Failed to process PDF';
          setExtractError(errorMsg);
          showToast(errorMsg, 'error');
        }
      }
    } catch (error) {
      // Only show error if not cancelled
      if (isProcessingRef.current && !abortController.signal.aborted) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to extract data';
        // Don't show toast for cancellation errors
        if (!errorMessage.includes('cancelled')) {
          setExtractError(errorMessage);
          showToast(errorMessage, 'error');
        }
      }
    } finally {
      // Only reset if this is still the active processing
      if (isProcessingRef.current && abortControllerRef.current === abortController) {
        setIsExtracting(false);
        isProcessingRef.current = false;
      }
    }
  };

  const handleReset = () => {
    // Cancel any ongoing polling
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isProcessingRef.current = false;

    setPdfFile(null);
    setExtractError(null);
    setJobStatus(null);
    setIsExtracting(false);
  };

  // Always disable body scroll to keep sheet full screen
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Cleanup: cancel polling on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      isProcessingRef.current = false;
    };
  }, []);

  const isSheetReady = jobStatus?.status === 'completed' && jobStatus.result?.sheet_url;
  const sheetUrl = jobStatus?.result?.sheet_url;



  return (
    <div className="h-screen overflow-hidden flex flex-col bg-white">

      {/* Main background container */}
      <div className="flex-1 relative overflow-hidden">
        <div className="w-full h-full main-bg"></div>
        {/* Dimming overlay - radial spotlight: lighter center, darker edges */}
        <div className="absolute inset-0"></div>
      </div>

      {/* Overlay Widget */}
      <div className="absolute inset-0 flex items-center justify-center p-4 z-20 pointer-events-none">
            <div className="pointer-events-auto animate-in fade-in max-h-90vh rounded-3xl overflow-hidden">
            <div className="w-full max-w-4xl lg:min-w-[500px] min-w-full bg-white rounded-3xl border-0 backdrop-blur-sm" style={{ boxShadow: '0 0 80px 20px rgba(255, 255, 255, 0.7), 0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
              {/* Widget Content */}
              <div className="lg:p-8 p-4">
                {isSheetReady && sheetUrl ? (
                  /* Success State */
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="w-full p-8 mb-6">
                      <div className="flex flex-col items-center">
                        {/* Transformation Indicator */}
                        <TransformationIndicator state="success" />

                        {/* Success Message */}
                        <h3 className="text-3xl font-bold text-emerald-900 mb-2">Your spreadsheet is ready!</h3>
                        <p className="text-base text-emerald-700">Data extracted and formatted in Google Sheets.</p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3 w-full max-w-md">
                      {/* Primary: Open Sheet Button */}
                      <a
                        href={`https://pdf2sheets.app/app?sheet=${encodeURIComponent(sheetUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full px-10 py-4 text-lg bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold rounded-lg shadow-md hover:from-emerald-600 hover:to-green-600 active:from-emerald-700 active:to-green-700 transition-all duration-200 focus:outline-none"
                        aria-label="Open your spreadsheet"
                      >
                        Open Sheet
                      </a>

                      {/* Secondary: Convert Another PDF */}
                      <button
                        onClick={handleReset}
                        className="w-full px-10 py-4 text-lg bg-white text-gray-700 font-semibold rounded-lg border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-200 focus:outline-none"
                        aria-label="Convert another PDF file"
                      >
                        Convert Another PDF
                      </button>
                    </div>
                  </div>
                ) : !pdfFile ? (
                  /* Upload State */
                  <PDFUpload onFileSelect={handleFileSelect} onError={handleUploadError} />
                ) : (
                  /* Processing State */
                  <PDFPreview
                    file={pdfFile}
                    onExtract={handleExtract}
                    onReset={handleReset}
                    isExtracting={isExtracting}
                    error={extractError}
                    jobStatus={jobStatus}
                  />
                )}
              </div>
            </div>
            </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
}

export default App;

